'use client';

import { c as _c } from "react/compiler-runtime";
import type { Fetchable } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { InterfaceChainUtils, type InterfaceToken, type Point, type Rewards } from '@morpho-org/dapp-core';
import { formatLongString } from '@morpho-org/morpho-ts';
import { Avatar, NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { V1Badge } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { TruncatedTooltip } from '@repo/ui';
import type { UnifiedVaultWarning } from '@/helpers/warnings';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { TransactionInfoCard } from '@/components/common/TransactionInfoCard';
import { getCollateralExposures, type VaultAllocationForCollateralExposure } from '@/helpers/collateralExposures';
import { formatAdjustedAmount } from '@/helpers/formatters';
import { getCuratorUrl } from '@/helpers/routing';
import { ButtonLink } from '../ButtonLink';
import { IconRewardHover } from '../IconRewardHover';
export interface VaultInfo {
  apy: bigint | null | undefined;
  netApy: bigint | null | undefined;
  performanceFee: bigint | null | undefined;
  allocation: VaultAllocationForCollateralExposure[];
  rewards: Rewards[];
  points: Fetchable<Point[]>;
  address: Address;
  curators: {
    name: string;
    image: string | null;
    id: string;
  }[];
  warnings: UnifiedVaultWarning[];
  name: string;
  asset: InterfaceToken;
  totalAssetsUsd: bigint | null | undefined;
}
export interface VaultTransactionInfoCardInnerProps {
  vault: VaultInfo;
  initialPosition: Fetchable<bigint>;
  finalPosition: Fetchable<bigint>;
}
const VaultTransactionInfoCardInner = t0 => {
  const $ = _c(72);
  const {
    initialPosition,
    finalPosition,
    vault
  } = t0;
  if (!vault) {
    throw new Error("Vault not found");
  }
  const chainId = vault.asset.chain.id;
  let t1;
  if ($[0] !== chainId) {
    t1 = InterfaceChainUtils.getMetadata(chainId);
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chain = t1;
  let t2;
  if ($[2] !== vault.allocation) {
    t2 = getCollateralExposures(vault.allocation);
    $[2] = vault.allocation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const collateralExposures = t2;
  const vaultName = vault.name ?? "";
  const {
    apy,
    netApy,
    performanceFee
  } = vault;
  let t3;
  if ($[4] !== netApy) {
    t3 = formatters.percent.digits(2).unit("%").max(10).of(netApy, 18);
    $[4] = netApy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedNetApy = t3;
  let t4;
  if ($[6] !== vault.asset.logoURI) {
    t4 = <Avatar size="medium" src={vault.asset.logoURI} />;
    $[6] = vault.asset.logoURI;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== vaultName) {
    t5 = ref => <span ref={ref} className="text-title-xs min-w-0 truncate">{vaultName}</span>;
    $[8] = vaultName;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5 || $[11] !== vaultName) {
    t6 = <TruncatedTooltip text={vaultName}>{t5}</TruncatedTooltip>;
    $[10] = t5;
    $[11] = vaultName;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <V1Badge />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6) {
    t8 = <div className="gap-xs flex min-w-0 items-center">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== vault.curators) {
    t9 = vault.curators.length > 0 && <div className="gap-2xs flex items-center max-sm:flex-wrap">{vault.curators.map(_temp)}</div>;
    $[16] = vault.curators;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className="gap-xs flex min-w-0 flex-col">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t4) {
    t11 = <TransactionInfoCard.Header>{t4}{t10}</TransactionInfoCard.Header>;
    $[21] = t10;
    $[22] = t4;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      size: "small"
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== chain.chain.id) {
    t13 = <Network chainId={chain.chain.id} srcAvatarProps={t12} />;
    $[25] = chain.chain.id;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== chain.chain.name) {
    t14 = <span className="text-3xs">{chain.chain.name}</span>;
    $[27] = chain.chain.name;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13 || $[30] !== t14) {
    t15 = <TransactionInfoCard.ListItem label="Network" value={<div className="gap-2xs flex flex-row items-center">{t13}{t14}</div>} />;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = vault.asset.symbol ?? "";
  let t17;
  if ($[32] !== t16) {
    t17 = formatLongString(t16);
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const t18 = `My Position (${t17})`;
  let t19;
  if ($[34] !== initialPosition) {
    t19 = {
      value: initialPosition
    };
    $[34] = initialPosition;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== finalPosition) {
    t20 = {
      value: finalPosition
    };
    $[36] = finalPosition;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== vault.asset.decimals || $[39] !== vault.asset.price) {
    t21 = collateral => formatAdjustedAmount({
      price: vault.asset.price,
      amount: collateral,
      decimals: vault.asset.decimals,
      withTrailingZero: false
    });
    $[38] = vault.asset.decimals;
    $[39] = vault.asset.price;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t19 || $[42] !== t20 || $[43] !== t21) {
    t22 = <TransactionInfoCard.ValueChange initialProps={t19} finalProps={t20} formatter={t21} />;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t18 || $[46] !== t22) {
    t23 = <TransactionInfoCard.ListItem label={t18} value={t22} />;
    $[45] = t18;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== vault.points) {
    t24 = vault.points ?? [];
    $[48] = vault.points;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== apy || $[51] !== netApy || $[52] !== performanceFee || $[53] !== t24 || $[54] !== vault.rewards) {
    t25 = {
      performanceFee,
      apy,
      rewards: vault.rewards,
      points: t24,
      variant: "vault",
      netApy
    };
    $[50] = apy;
    $[51] = netApy;
    $[52] = performanceFee;
    $[53] = t24;
    $[54] = vault.rewards;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== formattedNetApy) {
    t26 = <TransactionInfoCard.Value value={formattedNetApy} />;
    $[56] = formattedNetApy;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t25 || $[59] !== t26) {
    t27 = <TransactionInfoCard.ListItem label="APY" value={<div className="flex flex-row items-center gap-[2px]"><IconRewardHover RewardsHoverCardProps={t25}>{t26}</IconRewardHover></div>} />;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== collateralExposures || $[62] !== vault.totalAssetsUsd) {
    t28 = <TransactionInfoCard.ListItem label="Exposure" value={<CollateralExposureStack align="right" collateralExposures={collateralExposures} maxTokensCount={5} totalAssetsUsd={vault.totalAssetsUsd} />} />;
    $[61] = collateralExposures;
    $[62] = vault.totalAssetsUsd;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t15 || $[65] !== t23 || $[66] !== t27 || $[67] !== t28) {
    t29 = <TransactionInfoCard.List>{t15}{t23}{t27}{t28}</TransactionInfoCard.List>;
    $[64] = t15;
    $[65] = t23;
    $[66] = t27;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t11 || $[70] !== t29) {
    t30 = <TransactionInfoCard.Root>{t11}{t29}</TransactionInfoCard.Root>;
    $[69] = t11;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  return t30;
};
export default VaultTransactionInfoCardInner;
function _temp(curator) {
  return <ButtonLink key={curator.name} href={getCuratorUrl({
    curatorId: curator.id
  })} mode="external" buttonProps={{
    variant: "secondary",
    size: "sm",
    label: curator.name,
    avatarProps: {
      src: curator.image
    }
  }} />;
}